// Spectator presence: heartbeats into a room through the session-gated
// wrapper (spectate/presence.ts) and counts who is online. The stock
// `usePresence` hook cannot pass our `sessionToken`, so this is a small
// equivalent: heartbeat on an interval, list by room token, disconnect on
// unmount and (best effort, via sendBeacon) when the tab closes.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { CONVEX_URL } from '../env';
import { useSession } from './session';

const HEARTBEAT_MS = 10_000;

function newSessionId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

export type RoomPresence = { online: number; userIds: string[] };

export function usePresenceRoom(roomId: string): RoomPresence | undefined {
  const { token } = useSession();
  const heartbeat = useMutation(api.spectate.presence.heartbeat);
  const disconnect = useMutation(api.spectate.presence.disconnect);
  const [roomToken, setRoomToken] = useState<string | null>(null);
  const presenceToken = useRef<string | null>(null);
  const sessionId = useMemo(newSessionId, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const beat = async () => {
      try {
        const r = await heartbeat({ sessionToken: token, roomId, sessionId, interval: HEARTBEAT_MS });
        if (cancelled) return;
        presenceToken.current = r.sessionToken;
        setRoomToken(r.roomToken);
      } catch {
        // Presence is decorative; a failed beat retries on the next tick.
      }
    };
    void beat();
    const timer = window.setInterval(() => void beat(), HEARTBEAT_MS);
    const onHide = () => {
      const t = presenceToken.current;
      if (!t || !CONVEX_URL) return;
      const body = JSON.stringify({ path: 'spectate/presence:disconnect', args: { presenceSessionToken: t }, format: 'json' });
      try {
        navigator.sendBeacon(`${CONVEX_URL}/api/mutation`, new Blob([body], { type: 'application/json' }));
      } catch {
        // Ignored — the presence component times the session out anyway.
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('pagehide', onHide);
      const t = presenceToken.current;
      presenceToken.current = null;
      if (t) void disconnect({ presenceSessionToken: t }).catch(() => undefined);
    };
  }, [token, roomId, sessionId, heartbeat, disconnect]);

  const list = useQuery(api.spectate.presence.list, roomToken ? { roomToken } : 'skip');
  return useMemo(() => {
    if (!list) return undefined;
    const ids = Array.from(new Set(list.filter((p) => p.online).map((p) => p.userId)));
    return { online: ids.length, userIds: ids };
  }, [list]);
}
